import { useState, useEffect, useCallback } from "react";
import { fetchServices } from "../../api/services";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Spinner from "../common/Spinner";
import { formatUSD } from "../../utils/money";

export default function ServiceManagement() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      // For admin panel, we want to see ALL services (active and inactive)
      const { apiFetch } = await import("../../api/client");
      const results = await apiFetch("/api/services?include_inactive=true");
      setServices(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error("Failed to load services:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleEdit = async (service) => {
    setSelectedService(service);
    setEditModalOpen(true);
  };

  const handleDelete = async (service) => {
    setSelectedService(service);
    setDeleteModalOpen(true);
  };

  const toggleActive = async (service) => {
    try {
      // Use the API helper with proper data structure
      const { updateService } = await import("../../api/services");
      await updateService(service.id, { 
        ...service, 
        is_active: !service.is_active 
      });
      
      loadServices();
    } catch (error) {
      console.error("Failed to toggle service status:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-accent text-2xl font-bold text-birdie-charcoal">Service Management</h2>
          <p className="text-birdie-charcoal/70">Manage your services and pricing</p>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          className="bg-birdie-green hover:bg-birdie-green-dark"
        >
          <PlusIcon />
          Add Service
        </Button>
      </div>

      {/* Service List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading services..." />
        </div>
      ) : services.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-birdie-green/25 p-12 text-center">
          <ServiceIcon className="mx-auto h-12 w-12 text-birdie-warmgray" />
          <h3 className="mt-4 text-lg font-medium text-birdie-charcoal">No services found</h3>
          <p className="mt-2 text-birdie-charcoal/60">Get started by adding your first service</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <div
              key={service.id}
              className={`relative rounded-xl sm:rounded-lg border p-4 sm:p-6 shadow-sm transition-all hover:shadow-md ${
                service.is_active
                  ? "border-birdie-green/15 bg-white"
                  : "border-birdie-green/15 bg-birdie-beige opacity-75"
              }`}
            >
              {/* Status Badge */}
              <div className="absolute right-4 top-4">
                <button
                  onClick={() => toggleActive(service)}
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    service.is_active
                      ? "bg-birdie-green/10 text-birdie-green hover:bg-birdie-green/20"
                      : "bg-birdie-warmgray/20 text-birdie-warmgray hover:bg-birdie-warmgray/30"
                  }`}
                >
                  {service.is_active ? "Active" : "Inactive"}
                </button>
              </div>

              {/* Service Info */}
              <div className="pr-16">
                <h3 className="text-lg font-medium text-birdie-charcoal mb-2">
                  {service.name}
                </h3>

                {service.description && (
                  <p className="text-sm text-birdie-charcoal/70 mb-4 line-clamp-2">
                    {service.description}
                  </p>
                )}

                {/* Service Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-birdie-charcoal/60">Duration:</span>
                    <span className="font-medium">{service.duration_minutes} min</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-birdie-charcoal/60">Price:</span>
                    <span className="font-medium text-birdie-green">
                      {formatUSD(service.price_cents || 0)}
                    </span>
                  </div>

                  {service.deposit_cents > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-birdie-charcoal/60">Deposit:</span>
                      <span className="font-medium text-birdie-green">
                        {formatUSD(service.deposit_cents)}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-birdie-charcoal/60">Type:</span>
                    <span className="font-medium capitalize">
                      {service.service_type || "fixed"}
                    </span>
                  </div>

                  {service.pricing_mode === "per_unit" && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-birdie-charcoal/60">Per Unit:</span>
                        <span className="font-medium text-birdie-green">
                          {formatUSD(service.price_per_unit_cents || 0)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-birdie-charcoal/60">Units:</span>
                        <span className="font-medium">
                          {service.min_units || 1} - {service.max_units || "∞"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex items-center justify-end gap-2">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => handleEdit(service)}
                  className="text-birdie-green hover:text-birdie-green-dark"
                >
                  Edit
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => handleDelete(service)}
                  className="text-red-600 hover:text-red-900"
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <ServiceEditModal
        isOpen={editModalOpen}
        service={selectedService}
        onClose={(updated) => {
          setEditModalOpen(false);
          setSelectedService(null);
          if (updated) loadServices();
        }}
      />

      <ServiceDeleteModal
        isOpen={deleteModalOpen}
        service={selectedService}
        onClose={(deleted) => {
          setDeleteModalOpen(false);
          setSelectedService(null);
          if (deleted) loadServices();
        }}
      />

      <ServiceCreateModal
        isOpen={createModalOpen}
        onClose={(created) => {
          setCreateModalOpen(false);
          if (created) loadServices();
        }}
      />
    </div>
  );
}

// Edit/Create Modal Component
function ServiceEditModal({ isOpen, service, onClose }) {
  const isEdit = !!service;
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    duration_minutes: 60,
    price_cents: 0,
    deposit_cents: 0,
    service_type: "fixed",
    pricing_mode: "fixed",
    unit_label: "",
    unit_minutes: 60,
    price_per_unit_cents: 0,
    min_units: 1,
    max_units: null,
    sort_order: 0,
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || "",
        description: service.description || "",
        duration_minutes: service.duration_minutes || 60,
        price_cents: service.price_cents || 0,
        deposit_cents: service.deposit_cents || 0,
        service_type: service.service_type || "fixed",
        pricing_mode: service.pricing_mode || "fixed",
        unit_label: service.unit_label || "",
        unit_minutes: service.unit_minutes || 60,
        price_per_unit_cents: service.price_per_unit_cents || 0,
        min_units: service.min_units || 1,
        max_units: service.max_units || null,
        sort_order: service.sort_order || 0,
        is_active: service.is_active !== false
      });
    } else {
      // Reset for new service
      setFormData({
        name: "",
        description: "",
        duration_minutes: 60,
        price_cents: 0,
        deposit_cents: 0,
        service_type: "fixed",
        pricing_mode: "fixed",
        unit_label: "",
        unit_minutes: 60,
        price_per_unit_cents: 0,
        min_units: 1,
        max_units: null,
        sort_order: 0,
        is_active: true
      });
    }
    setError("");
  }, [service]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Service name is required");
      return;
    }

    setSaving(true);
    setError("");
    
    try {
      const url = isEdit ? `/api/services/${service.id}` : "/api/services";
      const method = isEdit ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      onClose(true);
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} service`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title={isEdit ? "Edit Service" : "Add New Service"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        
        {/* Basic Info */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-birdie-charcoal">Service Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-birdie-charcoal">Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-birdie-charcoal">Duration (minutes)</label>
            <input
              type="number"
              min="1"
              value={formData.duration_minutes}
              onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
              className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-birdie-charcoal">Service Type</label>
            <select
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
            >
              <option value="fixed">Fixed Duration</option>
              <option value="block">Block Booking</option>
              <option value="multi_day">Multi-Day</option>
              <option value="recurring">Recurring</option>
            </select>
          </div>
        </div>

        {/* Pricing */}
        <div className="border-t border-birdie-green/15 pt-6">
          <h4 className="font-accent text-lg font-medium text-birdie-charcoal mb-4">Pricing</h4>
          
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-birdie-charcoal">Pricing Mode</label>
              <select
                value={formData.pricing_mode}
                onChange={(e) => setFormData({ ...formData, pricing_mode: e.target.value })}
                className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
              >
                <option value="fixed">Fixed Price</option>
                <option value="per_unit">Per Unit</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-birdie-charcoal">
                {formData.pricing_mode === "fixed" ? "Base Price ($)" : "Price per Unit ($)"}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.pricing_mode === "fixed" ? 
                  (formData.price_cents / 100).toFixed(2) : 
                  (formData.price_per_unit_cents / 100).toFixed(2)}
                onChange={(e) => {
                  const cents = Math.round(parseFloat(e.target.value) * 100) || 0;
                  if (formData.pricing_mode === "fixed") {
                    setFormData({ ...formData, price_cents: cents });
                  } else {
                    setFormData({ ...formData, price_per_unit_cents: cents });
                  }
                }}
                className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-birdie-charcoal">Deposit Required ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={(formData.deposit_cents / 100).toFixed(2)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  deposit_cents: Math.round(parseFloat(e.target.value) * 100) || 0 
                })}
                className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-birdie-charcoal">Sort Order</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
              />
            </div>
          </div>
        </div>

        {/* Per-Unit Settings */}
        {formData.pricing_mode === "per_unit" && (
          <div className="border-t border-birdie-green/15 pt-6">
            <h4 className="font-accent text-lg font-medium text-birdie-charcoal mb-4">Unit Settings</h4>
            
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-birdie-charcoal">Unit Label</label>
                <input
                  type="text"
                  placeholder="e.g., hours, days, people"
                  value={formData.unit_label}
                  onChange={(e) => setFormData({ ...formData, unit_label: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-birdie-charcoal">Minutes per Unit</label>
                <input
                  type="number"
                  min="1"
                  value={formData.unit_minutes}
                  onChange={(e) => setFormData({ ...formData, unit_minutes: parseInt(e.target.value) || 0 })}
                  className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-birdie-charcoal">Min Units</label>
                <input
                  type="number"
                  min="1"
                  value={formData.min_units}
                  onChange={(e) => setFormData({ ...formData, min_units: parseInt(e.target.value) || 1 })}
                  className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-birdie-charcoal">Max Units (leave empty for unlimited)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_units || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_units: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
                />
              </div>
            </div>
          </div>
        )}

        {/* Status */}
        <div className="border-t border-birdie-green/15 pt-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-birdie-green/25"
            />
            <span className="text-sm font-medium text-birdie-charcoal">
              Service is active and available for booking
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : isEdit ? "Save Changes" : "Create Service"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Create Modal Component (reuses ServiceEditModal)
function ServiceCreateModal({ isOpen, onClose }) {
  return (
    <ServiceEditModal
      isOpen={isOpen}
      service={null}
      onClose={onClose}
    />
  );
}

// Delete Modal Component
function ServiceDeleteModal({ isOpen, service, onClose }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [isOpen]);

  const handleDelete = async () => {
    setDeleting(true);
    setError("");

    try {
      // Use the API helper instead of raw fetch
      const { deleteService } = await import("../../api/services");
      await deleteService(service.id);
      
      onClose(true);
    } catch (err) {
      setError(err.message || "Failed to delete service");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)} title="Delete Service">
      <div className="space-y-4">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <WarningIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Delete Service</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  This will permanently delete <strong>{service?.name}</strong>.
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting ? <Spinner size="sm" /> : "Delete Service"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Icons
function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ServiceIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}

function WarningIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );
}
